/**
 * One-shot "flash" message shown on the login page after an auth action
 * that revokes the current session and bounces the user to /auth/login.
 *
 * Why not React Router location state? The redirect to /auth/login is
 * driven by ProtectedRoute reacting to `user` becoming null (see
 * AuthContext.changePassword), and ProtectedRoute controls that
 * navigation's state (`{ from }`) — a caller can't attach its own message
 * to it without racing the declarative <Navigate>. sessionStorage sidesteps
 * the race: the caller writes the message *before* the session is cleared,
 * and LoginPage reads-and-clears it on mount regardless of which navigation
 * won. sessionStorage (not localStorage) so it never outlives the tab.
 */
const AUTH_FLASH_KEY = 'fias:auth-flash';

export function setAuthFlash(message: string): void {
  try {
    sessionStorage.setItem(AUTH_FLASH_KEY, message);
  } catch {
    // Private-mode / storage-disabled: a missing confirmation banner is a
    // cosmetic loss, never a functional one.
  }
}

export function clearAuthFlash(): void {
  try {
    sessionStorage.removeItem(AUTH_FLASH_KEY);
  } catch {
    // ignore — see setAuthFlash
  }
}

/** Reads the pending flash message and clears it (one-shot). */
export function takeAuthFlash(): string | null {
  try {
    const message = sessionStorage.getItem(AUTH_FLASH_KEY);
    if (message !== null) sessionStorage.removeItem(AUTH_FLASH_KEY);
    return message;
  } catch {
    return null;
  }
}
